import { createSelector } from 'reselect'
import { isLEHotelItem } from 'lib/checkout/checkoutUtils'
import { getAllOffers } from 'selectors/offerSelectors'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { isSpoofed } from 'selectors/featuresSelectors'
import { isE2EUser } from 'lib/checkout/form'
import { getAccommodationBreakdownView } from 'checkout/selectors/view/accommodation'
import { checkoutWithMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'

// Payment schedule template for flash hotels >= $1999 AUD
export const FLASH_PAYMENT_SCHEDULE_TEMPLATE_MIN_1999 = '001_hotel_50_percentage_dynamic_1999_AUD'

// Minimum flash hotel offer value to apply payment schedules (AUD only)
const FLASH_PAYMENT_SCHEDULE_MINIMUM_OFFER_VALUE = 1999

const isFlashHotelTotalAboveThreshold = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getAllOffers(state),
  (state: App.State) => getAccommodationBreakdownView(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (items, offers, accommodationBreakdownView, useMemberPrice): boolean => {
    const leHotelItems = items.filter(isLEHotelItem)

    const flashHotelTotal = leHotelItems.reduce((sum, item) => {
      const offer = offers[item.offerId]
      if (!isFlashHotelOffer(offer)) {
        return sum
      }

      const hotelBreakdownView = accommodationBreakdownView.data.accommodations.find(
        view => view.items.some(itemView => itemView.itemId === item.itemId),
      )
      const hotelItemView = hotelBreakdownView?.items.find(itemView => itemView.itemId === item.itemId)
      const price = (useMemberPrice && hotelItemView?.memberPrice) ? hotelItemView?.memberPrice : hotelItemView?.price ?? 0
      const total = price + (hotelItemView?.taxesAndFees ?? 0)

      return sum + total
    }, 0)

    return flashHotelTotal >= FLASH_PAYMENT_SCHEDULE_MINIMUM_OFFER_VALUE
  },
)

/**
 * Checks if flash payment schedule should be applied based on:
 * - User is not spoofed (spoofed users are excluded but still get min_1999 template)
 * - User is not an E2E test user
 * - Total of all flash hotel items >= 1999 AUD
 * - Currency is AUD
 */
export const shouldTriggerFlashPaymentScheduleExperiment = createSelector(
  isFlashHotelTotalAboveThreshold,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => isSpoofed(state),
  (state: App.State) => state.auth.account.email,
  (flashHotelTotalAboveThreshold, currency, userIsSpoofed, userEmail): boolean => {
    if (userIsSpoofed) {
      return false
    }

    if (isE2EUser(userEmail ?? '')) {
      return false
    }

    if (currency !== 'AUD') {
      return false
    }

    if (!flashHotelTotalAboveThreshold) {
      return false
    }

    return true
  },
)

/**
 * Checks if spoofed user meets the base conditions for template override (excluding spoof check).
 * Used to determine if a spoofed user should get the min_1999 template.
 */
const meetsBaseConditionsForTemplateOverride = createSelector(
  isFlashHotelTotalAboveThreshold,
  (state: App.State) => state.geo.currentCurrency,
  (flashHotelTotalAboveThreshold, currency): boolean => {
    return currency === 'AUD' && flashHotelTotalAboveThreshold
  },
)

/**
 * Gets the template ID override for flash hotel payment schedules.
 * Returns the $1999 template when eligibility conditions are met, undefined otherwise.
 *
 * Important distinctions:
 * - Payment schedule rebooking active → returns undefined (use original order's template)
 * - Spoofed user meeting base conditions → returns min_1999 template
 * - Eligible user (AUD, >= $1999, flash hotel) → returns min_1999 template
 * - Conditions NOT met → returns undefined (use offer's template)
 */
export const getFlashPaymentScheduleTemplateOverride = createSelector(
  (state: App.State) => isSpoofed(state),
  meetsBaseConditionsForTemplateOverride,
  shouldTriggerFlashPaymentScheduleExperiment,
  (state: App.State) => state.checkout.payment.paymentScheduleRebookingId,
  (userIsSpoofed, meetsBaseConditions, shouldApplyTemplate, paymentScheduleRebookingId): string | undefined => {
    // Skip template override when rebooking - respect the original order's schedule type
    if (paymentScheduleRebookingId) {
      return undefined
    }

    // Spoofed users always get min_1999 template if they meet base conditions
    if (userIsSpoofed && meetsBaseConditions) {
      return FLASH_PAYMENT_SCHEDULE_TEMPLATE_MIN_1999
    }

    // Apply template if eligible (AUD currency, >= $1999, flash hotel, not E2E/spoofed)
    if (shouldApplyTemplate) {
      return FLASH_PAYMENT_SCHEDULE_TEMPLATE_MIN_1999
    }

    return undefined
  },
)
